import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
	Modal,
	Text,
	Group,
	Box,
	Select,
	TextInput,
	Button,
	Divider,
	ActionIcon,
} from '@mantine/core'
import Plus from 'tabler-icons-react/dist/icons/plus'
import Trash from 'tabler-icons-react/dist/icons/trash'
import Pencil from 'tabler-icons-react/dist/icons/pencil'
import { getItemOptions } from '../services/actions/experienceActions'

const PurchasedItemsModal = ({
	show,
	onClose,
	onSave,
	experienceSlug,
	currentItems,
	currentUserId,
}) => {
	const { id } = useParams()
	const isCreating = id === 'new'
	const [allOptions, setAllOptions] = useState([])
	const [selectedItems, setSelectedItems] = useState([])
	const [addingNew, setAddingNew] = useState(false)
	const [newItemId, setNewItemId] = useState(null)
	const [paymentMethod, setPaymentMethod] = useState('None')
	const [paymentAmount, setPaymentAmount] = useState('')
	const [editingIndex, setEditingIndex] = useState(null)

	useEffect(() => {
		if (show) {
			getItemOptions(experienceSlug)
				.then((options) => setAllOptions(options))
				.catch((err) => console.error('Failed to fetch item options:', err))
		}
	}, [show, experienceSlug])

	useEffect(() => {
		if (show) {
			setSelectedItems([...currentItems])
			setAddingNew(false)
			setNewItemId(null)
			setPaymentMethod('None')
			setPaymentAmount('')
			setEditingIndex(null)
		}
	}, [currentItems, show])

	const handleDelete = (id) => {
		const item = selectedItems.find((item) => item.id === id)
		if (item?.issued) return
		if (window.confirm('Are you sure you want to remove this item?')) {
			setSelectedItems((prev) => prev.filter((i) => i.id !== id))
		}
	}

	const handleEdit = (index) => {
		const item = selectedItems[index]
		setEditingIndex(index)
		setNewItemId(item.id)
		setPaymentMethod(item.paymentMethod || 'None')
		setPaymentAmount(item.paymentAmount || '')
		setAddingNew(true)
	}

	const handleAddOrUpdate = () => {
		if (!newItemId) {
			alert('Please select an item.')
			return
		}

		if (!paymentMethod) {
			alert('Please select a payment method.')
			return
		}

		if (paymentMethod !== 'None' && !paymentAmount.trim()) {
			alert('Please enter the payment amount.')
			return
		}

		const option = allOptions.find((opt) => opt.id === newItemId)
		if (!option) return

		const updated = [...selectedItems]
		const itemData = {
			id: option.id,
			name: option.name,
			imageUrl: option.imageUrl || '',
			issued: false,
			paymentMethod,
			paymentAmount: paymentMethod === 'None' ? null : paymentAmount,
			addedByUser:
				paymentMethod && paymentMethod !== 'None' ? currentUserId : '',
		}

		if (editingIndex !== null) {
			updated[editingIndex] = itemData
		} else {
			updated.push(itemData)
		}

		setSelectedItems(updated)
		setNewItemId(null)
		setPaymentMethod('None')
		setPaymentAmount('')
		setAddingNew(false)
		setEditingIndex(null)
	}

	const handleSubmit = () => {
		onSave(selectedItems)
		onClose()
	}

	const remainingOptions = allOptions.filter(
		(opt) =>
			opt.allowAddToCheckIn &&
			!selectedItems.find(
				(item, idx) => item.id === opt.id && idx !== editingIndex
			)
	)

	return (
		<Modal
			opened={show}
			onClose={onClose}
			title='Edit Purchased Items'
			centered
			size='lg'
		>
			{selectedItems.length === 0 && (
				<Text color='dimmed' fs='italic' mb='md'>
					No items selected.
				</Text>
			)}

			{selectedItems.map((item, i) => (
				<Group key={item.id} position='apart' mb='xs'>
					<Text>
						{item.name}{' '}
						{item.paymentMethod && item.paymentMethod !== 'None'
							? `(Paid $${item.paymentAmount || 0} via ${item.paymentMethod})`
							: ''}
					</Text>
					{!item.issued && (
						<Group spacing={4}>
							<ActionIcon size='sm' onClick={() => handleEdit(i)}>
								<Pencil size={14} />
							</ActionIcon>
							<ActionIcon
								color='red'
								size='sm'
								onClick={() => handleDelete(item.id)}
							>
								<Trash size={14} />
							</ActionIcon>
						</Group>
					)}
				</Group>
			))}

			{addingNew ? (
				<Box mt='md'>
					<Select
						label='Item'
						data={remainingOptions.map((opt) => ({
							value: opt.id,
							label: opt.name,
						}))}
						value={newItemId}
						onChange={setNewItemId}
						placeholder='Select an item'
						required
						mb='sm'
						disabled={editingIndex !== null}
					/>
					<Select
						label='Payment Method'
						data={['None', 'Venmo', 'Cash']}
						value={paymentMethod}
						onChange={setPaymentMethod}
						required
						mb='sm'
					/>
					{paymentMethod !== 'None' && (
						<TextInput
							label='Amount Paid'
							value={paymentAmount}
							onChange={(e) => setPaymentAmount(e.target.value)}
							placeholder='$0.00'
							mb='sm'
							required
						/>
					)}
					<Group position='right'>
						<Button
							variant='subtle'
							size='xs'
							onClick={() => {
								setAddingNew(false)
								setNewItemId(null)
								setPaymentMethod('None')
								setPaymentAmount('')
								setEditingIndex(null)
							}}
						>
							Cancel
						</Button>
						<Button size='xs' onClick={handleAddOrUpdate}>
							{editingIndex !== null ? 'Update' : 'Add'}
						</Button>
					</Group>
				</Box>
			) : (
				<Button
					variant='outline'
					leftIcon={remainingOptions.length === 0 ? null : <Plus size={16} />}
					size='xs'
					mt='md'
					onClick={() => {
						setAddingNew(true)
						setEditingIndex(null)
					}}
					disabled={remainingOptions.length === 0}
				>
					{remainingOptions.length === 0
						? 'No Options Available to Add'
						: 'Add Purchased Item'}
				</Button>
			)}

			<Divider my='md' />

			<Group position='right'>
				<Button onClick={handleSubmit}>{isCreating ? 'Done' : 'Save'}</Button>
			</Group>
		</Modal>
	)
}

export default PurchasedItemsModal
